import { useCallback, useEffect, useRef, useState } from 'react';

type Track = 'original' | 'mastered';

interface ComparePlayerProps {
    title: string;
    originalUrl: string;
    masteredUrl: string;
}

function formatTime(seconds: number): string {
    if (!Number.isFinite(seconds)) {
        return '0:00';
    }
    const total = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * A/B preview player: both snippets are loaded and kept in sync, so switching
 * between "original" and "mastered" is instant and always at the same position.
 *
 * Replaces the legacy hand written MediaPlayer lib.
 */
export function ComparePlayer({ title, originalUrl, masteredUrl }: ComparePlayerProps) {
    const originalRef = useRef<HTMLAudioElement>(null);
    const masteredRef = useRef<HTMLAudioElement>(null);

    const [active, setActive] = useState<Track>('mastered');
    const [playing, setPlaying] = useState(false);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(0);

    const activeEl = useCallback(
        () => (active === 'mastered' ? masteredRef.current : originalRef.current),
        [active],
    );

    const otherEl = useCallback(
        () => (active === 'mastered' ? originalRef.current : masteredRef.current),
        [active],
    );

    // Reset when a new job is previewed.
    useEffect(() => {
        setPlaying(false);
        setPosition(0);
        setActive('mastered');
    }, [originalUrl, masteredUrl]);

    const toggle = useCallback(async () => {
        const el = activeEl();
        if (!el) return;
        if (playing) {
            el.pause();
            setPlaying(false);
            return;
        }
        try {
            await el.play();
            setPlaying(true);
        } catch {
            setPlaying(false);
        }
    }, [activeEl, playing]);

    const switchTo = useCallback(
        async (track: Track) => {
            if (track === active) return;
            const current = activeEl();
            const next = track === 'mastered' ? masteredRef.current : originalRef.current;
            const at = current?.currentTime ?? 0;

            current?.pause();
            setActive(track);

            if (next) {
                next.currentTime = at;
                if (playing) {
                    try {
                        await next.play();
                    } catch {
                        setPlaying(false);
                    }
                }
            }
        },
        [active, activeEl, playing],
    );

    const seek = useCallback(
        (value: number) => {
            setPosition(value);
            const current = activeEl();
            const other = otherEl();
            if (current) current.currentTime = value;
            if (other) other.currentTime = value;
        },
        [activeEl, otherEl],
    );

    return (
        <div className="player">
            <audio
                ref={originalRef}
                src={originalUrl}
                preload="auto"
                onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
                onTimeUpdate={(event) => active === 'original' && setPosition(event.currentTarget.currentTime)}
                onEnded={() => setPlaying(false)}
            />
            <audio
                ref={masteredRef}
                src={masteredUrl}
                preload="auto"
                onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
                onTimeUpdate={(event) => active === 'mastered' && setPosition(event.currentTarget.currentTime)}
                onEnded={() => setPlaying(false)}
            />

            <div className="player__head">
                <div>
                    <p className="player__label">Free preview</p>
                    <h3 className="player__title">{title}</h3>
                </div>
                <div className="player__switch" role="group" aria-label="Compare original and mastered">
                    <button
                        type="button"
                        className={active === 'original' ? 'is-active' : ''}
                        onClick={() => void switchTo('original')}
                    >
                        Original
                    </button>
                    <button
                        type="button"
                        className={active === 'mastered' ? 'is-active' : ''}
                        onClick={() => void switchTo('mastered')}
                    >
                        Mastered
                    </button>
                </div>
            </div>

            <div className="player__controls">
                <button
                    type="button"
                    className="player__play"
                    onClick={() => void toggle()}
                    aria-label={playing ? 'Pause' : 'Play'}
                >
                    {playing ? (
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                            <rect x="6" y="5" width="4" height="14" rx="1" />
                            <rect x="14" y="5" width="4" height="14" rx="1" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                            <path d="M8 5.5v13l11-6.5-11-6.5z" />
                        </svg>
                    )}
                </button>

                <input
                    className="player__seek"
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.01}
                    value={Math.min(position, duration || 0)}
                    onChange={(event) => seek(Number(event.target.value))}
                    aria-label="Seek"
                />

                <span className="player__time">
                    {formatTime(position)} / {formatTime(duration)}
                </span>
            </div>

            <p className="player__note">
                The preview is a short snippet. Unlock the full length master below.
            </p>
        </div>
    );
}
